import React, { useState, useEffect } from 'react';
import { User, Bell, Lock, CreditCard, Database, Mail, Globe, Zap, Save, Loader2, Check, Trophy, EyeOff } from 'lucide-react';
import { fetchAuthSession, updatePassword } from 'aws-amplify/auth';
import { ProfileView } from './ProfileView';

type SettingsTab = 'profile' | 'notifications' | 'security' | 'billing' | 'integrations';

interface NotificationEvent {
  email: boolean;
  in_app: boolean;
}

interface NotificationSettingsData {
  available_events: string[];
  settings: {
    [key: string]: NotificationEvent;
  };
}

const EVENT_LABELS: { [key: string]: string } = {
  'dfg_order_created': 'New DFG Order Created',
  'dfg_order_status_changed': 'DFG Order Status Changed',
  'dfg_message_from_admin': 'Message from Admin (DFG)',
  'dfg_message_from_user': 'Message from Student (DFG)',
  'dfg_order_delivered': 'DFG Order Delivered',
  'jv_submission_created': 'New JV Submission',
  'jv_status_changed': 'JV Submission Status Changed',
  'jv_message_from_admin': 'Message from Admin (JV)',
  'jv_message_from_user': 'Message from User (JV)',
  'jv_admin_only_comment': 'Internal Admin Comments (JV)',
  'jv_escalation': 'Escalation Requests',
  'jv_sla_breach_warning': 'SLA Breach Warning',
  'jv_sla_breach_final': 'Final SLA Breach Warning',
  'user_created': 'New User Created',
  'admin_created': 'New Admin Created',
  'admin_removed': 'Admin Removed',
  'tax_list_order_created': 'Tax List Order Created',
  'tax_list_delivered': 'Tax List Delivered',
  'payment_success': 'Payment Successful',
  'payment_failed': 'Payment Failed',
  'system_error': 'System Error',
  'api_failure': 'API Failure',
  'database_issue': 'Database Issue'
};

const EVENT_CATEGORIES: { [key: string]: string[] } = {
  'DFG Orders': [
    'dfg_order_created',
    'dfg_order_status_changed',
    'dfg_message_from_admin',
    'dfg_message_from_user',
    'dfg_order_delivered'
  ],
  'JV Submissions': [
    'jv_submission_created',
    'jv_status_changed',
    'jv_message_from_admin',
    'jv_message_from_user',
    'jv_escalation',
    'jv_sla_breach_warning',
    'jv_sla_breach_final'
  ],
  'User Management': [
    'user_created',
    'admin_created',
    'admin_removed'
  ],
  'Tax Lists': [
    'tax_list_order_created',
    'tax_list_delivered',
    'payment_success',
    'payment_failed'
  ]
};

/**
 * Standalone opt-out card for the leaderboard.
 *
 * Persisted to localStorage because the backend field doesn't exist yet —
 * this ships the UX today and swaps for a real preferences API when one
 * lands. Key is namespaced so it survives alongside other tdb.* prefs.
 */
const LEADERBOARD_OPTOUT_KEY = 'tdb.leaderboardOptOut';

function LeaderboardVisibilityCard() {
  const [optedOut, setOptedOut] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(LEADERBOARD_OPTOUT_KEY) === 'true';
  });

  const toggle = (next: boolean) => {
    setOptedOut(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LEADERBOARD_OPTOUT_KEY, String(next));
      window.dispatchEvent(new CustomEvent('tdb:leaderboard-optout-changed', { detail: next }));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-gradient-to-br from-tdb-accent to-purple-600 rounded-xl shadow-lg">
          <Trophy className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-tdb-primary">Leaderboard visibility</h2>
          <p className="text-sm text-gray-500 mt-1">
            Control whether your name appears on the student leaderboard.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-4 p-5 rounded-xl border-2 border-gray-200
                        hover:border-tdb-accent/40 transition-colors cursor-pointer">
        <input
          type="checkbox"
          checked={optedOut}
          onChange={(e) => toggle(e.target.checked)}
          className="mt-1 w-5 h-5 text-tdb-accent rounded-md focus:ring-2 focus:ring-tdb-accent
                     focus:ring-offset-2 cursor-pointer"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 font-semibold text-gray-800">
            <EyeOff className="w-4 h-4 text-gray-500" />
            Hide me from the leaderboard
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Your own stats still appear when you visit the page — only your row is removed
            from the ranked list and the "around you" slice. You can turn this back on any time.
          </p>
        </div>
      </label>

      {optedOut && (
        <p className="text-xs text-gray-500 mt-3">
          You are currently hidden. Other students will not see your name in the rankings.
        </p>
      )}
    </div>
  );
}

interface SettingsViewProps {
  initialParams?: { tab?: string };
}

export function SettingsView({ initialParams }: SettingsViewProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>((initialParams?.tab as SettingsTab) || 'profile');

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettingsData | null>(null);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationSuccess, setNotificationSuccess] = useState<string | null>(null);

  // Change-password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Load notification settings when notifications tab is selected
  useEffect(() => {
    if (activeTab === 'notifications' && !notificationSettings) {
      loadNotificationSettings();
    }
  }, [activeTab]);

  const loadNotificationSettings = async () => {
    try {
      setNotificationLoading(true);
      setNotificationError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        setNotificationError('Not authenticated');
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) throw new Error('VITE_API_URL not configured');
      const response = await fetch(`${apiUrl}/notification-settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotificationSettings(data);
      } else {
        const errorData = await response.json();
        setNotificationError(errorData.error || 'Failed to load settings');
      }
    } catch (err) {
      console.error('Error loading notification settings:', err);
      setNotificationError('Failed to load settings');
    } finally {
      setNotificationLoading(false);
    }
  };

  const saveNotificationSettings = async () => {
    if (!notificationSettings) return;

    try {
      setNotificationSaving(true);
      setNotificationError(null);
      setNotificationSuccess(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        setNotificationError('Not authenticated');
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) throw new Error('VITE_API_URL not configured');
      const response = await fetch(`${apiUrl}/notification-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: notificationSettings.settings })
      });

      if (response.ok) {
        setNotificationSuccess('Preferences saved successfully!');
        setTimeout(() => setNotificationSuccess(null), 5000);
      } else {
        const errorData = await response.json();
        setNotificationError(errorData.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving notification settings:', err);
      setNotificationError('Failed to save settings');
    } finally {
      setNotificationSaving(false);
    }
  };

  // Local password-strength check (mirrors the Activate flow's rules).
  const passwordProblem = (pw: string): string | null => {
    if (pw.length < 8) return 'Use at least 8 characters.';
    if (!/[a-z]/.test(pw)) return 'Add a lowercase letter.';
    if (!/[A-Z]/.test(pw)) return 'Add an uppercase letter.';
    if (!/[0-9]/.test(pw)) return 'Add a number.';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Add a symbol (like ! or #).';
    return null;
  };

  const handleUpdatePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword) { setPasswordError('Enter your current password.'); return; }
    const problem = passwordProblem(newPassword);
    if (problem) { setPasswordError(problem); return; }
    if (newPassword !== confirmPassword) { setPasswordError('The new passwords don’t match.'); return; }
    if (newPassword === currentPassword) { setPasswordError('Your new password must be different from the current one.'); return; }

    try {
      setPasswordSaving(true);
      // Cognito password change for the signed-in user — client-side via Amplify, no backend endpoint.
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setPasswordSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(null), 5000);
    } catch (err: any) {
      const name = err?.name || '';
      if (name === 'NotAuthorizedException') {
        setPasswordError('Your current password is incorrect.');
      } else if (name === 'InvalidPasswordException' || name === 'InvalidParameterException') {
        setPasswordError(err?.message || 'That new password wasn’t accepted. Try a stronger one.');
      } else if (name === 'LimitExceededException') {
        setPasswordError('Too many attempts. Please wait a bit and try again.');
      } else {
        setPasswordError(err?.message || 'Couldn’t update your password. Please try again.');
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const toggleNotificationSetting = (eventType: string, channel: 'email' | 'in_app') => {
    if (!notificationSettings) return;

    setNotificationSettings({
      ...notificationSettings,
      settings: {
        ...notificationSettings.settings,
        [eventType]: {
          ...notificationSettings.settings[eventType],
          [channel]: !notificationSettings.settings[eventType][channel]
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-lg p-4 space-y-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'profile'
                  ? 'bg-tdb-accent/10 text-tdb-accent'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <User className="w-5 h-5" />
              <span>Profile</span>
            </button>

            <button
              onClick={() => setActiveTab('notifications')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'notifications'
                  ? 'bg-tdb-accent/10 text-tdb-accent'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <Bell className="w-5 h-5" />
              <span>Notifications</span>
            </button>

            <button
              onClick={() => setActiveTab('security')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'security'
                  ? 'bg-tdb-accent/10 text-tdb-accent'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <Lock className="w-5 h-5" />
              <span>Security</span>
            </button>

            <button
              disabled
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium text-gray-400 cursor-not-allowed opacity-50"
            >
              <CreditCard className="w-5 h-5" />
              <span>Billing</span>
            </button>

            <button
              disabled
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium text-gray-400 cursor-not-allowed opacity-50"
            >
              <Database className="w-5 h-5" />
              <span>Integrations</span>
            </button>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile - Uses shared ProfileView component */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <ProfileView />
              <LeaderboardVisibilityCard />
            </div>
          )}

          {/* Notification Preferences */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-gradient-to-br from-tdb-accent to-purple-600 rounded-xl shadow-lg">
                    <Bell className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-tdb-accent to-purple-600 bg-clip-text text-transparent">Notification Preferences</h2>
                    <p className="text-sm text-gray-500 mt-1">Customize how you receive updates</p>
                  </div>
                </div>

                {/* Error Message */}
                {notificationError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
                    {notificationError}
                  </div>
                )}

                {/* Success Message */}
                {notificationSuccess && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 text-green-900 px-8 py-5 rounded-xl mb-6 flex items-center gap-4 shadow-lg">
                    <div className="p-3 bg-green-500 rounded-full shadow-md">
                      <Check className="w-6 h-6 text-white font-bold" />
                    </div>
                    <span className="font-bold text-lg">{notificationSuccess}</span>
                  </div>
                )}

                {/* Loading State */}
                {notificationLoading && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-tdb-accent" />
                    <span className="ml-3 text-lg text-gray-600">Loading notification settings...</span>
                  </div>
                )}

                {/* Settings Grid */}
                {!notificationLoading && notificationSettings && (
                  <>
                    <div className="space-y-5">
                      {Object.entries(EVENT_CATEGORIES).map(([category, events]) => {
                        // Filter to only show events available to this user
                        const availableEvents = events.filter(e => notificationSettings.available_events.includes(e));

                        if (availableEvents.length === 0) return null;

                        return (
                          <div key={category} className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                            <h3 className="font-bold text-xl mb-5 text-gray-800 flex items-center gap-2">
                              <span className="w-2 h-8 bg-gradient-to-b from-tdb-accent to-purple-600 rounded-full"></span>
                              {category}
                            </h3>
                            <div className="space-y-1">
                              {availableEvents.map(eventType => {
                                const eventSettings = notificationSettings.settings[eventType] || { email: true, in_app: true };

                                return (
                                  <div key={eventType} className="flex items-center justify-between py-4 px-4 rounded-xl hover:bg-white transition-colors group">
                                    <span className="text-gray-700 font-medium text-base group-hover:text-tdb-accent transition-colors">
                                      {EVENT_LABELS[eventType] || eventType}
                                    </span>
                                    <div className="flex gap-6">
                                      {/* Email Toggle */}
                                      <label className="flex items-center gap-3 cursor-pointer group/toggle">
                                        <input
                                          type="checkbox"
                                          checked={eventSettings.email}
                                          onChange={() => toggleNotificationSetting(eventType, 'email')}
                                          className="w-5 h-5 text-tdb-accent rounded-md focus:ring-2 focus:ring-tdb-accent focus:ring-offset-2 cursor-pointer transition-all"
                                        />
                                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 group-hover/toggle:bg-tdb-accent group-hover/toggle:text-white transition-all">
                                          <Mail className="w-4 h-4" />
                                          <span className="text-sm font-medium">Email</span>
                                        </div>
                                      </label>

                                      {/* In-App Toggle */}
                                      <label className="flex items-center gap-3 cursor-pointer group/toggle">
                                        <input
                                          type="checkbox"
                                          checked={eventSettings.in_app}
                                          onChange={() => toggleNotificationSetting(eventType, 'in_app')}
                                          className="w-5 h-5 text-purple-600 rounded-md focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 cursor-pointer transition-all"
                                        />
                                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 group-hover/toggle:bg-purple-600 group-hover/toggle:text-white transition-all">
                                          <Bell className="w-4 h-4" />
                                          <span className="text-sm font-medium">In-App</span>
                                        </div>
                                      </label>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Save Button */}
                    <div className="mt-8 flex justify-end pt-6 border-t-2 border-gray-200">
                      <button
                        onClick={saveNotificationSettings}
                        disabled={notificationSaving}
                        className="px-8 py-3 bg-gradient-to-r from-tdb-accent to-purple-600 text-white rounded-xl hover:from-tdb-accent/90 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 font-semibold shadow-lg hover:shadow-xl hover:scale-105 disabled:hover:scale-100"
                      >
                        {notificationSaving ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Saving Changes...
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5" />
                            Save Preferences
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Change Password */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
                  <Lock className="w-5 h-5 text-tdb-accent" />
                  <span>Change Password</span>
                </h2>

                <form
                  className="space-y-4"
                  onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }}
                >
                  <div>
                    <label className="block text-sm font-semibold text-tdb-primary mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      autoComplete="current-password"
                      data-testid="settings-current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-tdb-primary mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      data-testid="settings-new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      At least 8 characters with an uppercase &amp; lowercase letter, a number, and a symbol.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-tdb-primary mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      data-testid="settings-confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                    />
                  </div>

                  {passwordError && (
                    <p data-testid="settings-password-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                      {passwordError}
                    </p>
                  )}
                  {passwordSuccess && (
                    <p data-testid="settings-password-success" className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2 flex items-center space-x-2">
                      <Check className="w-4 h-4" />
                      <span>{passwordSuccess}</span>
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="settings-update-password"
                    disabled={passwordSaving}
                    className="w-full md:w-auto px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all font-bold shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {passwordSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>{passwordSaving ? 'Updating…' : 'Update Password'}</span>
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Billing Settings */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              {/* Current Plan */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
                  <CreditCard className="w-5 h-5 text-tdb-accent" />
                  <span>Current Plan</span>
                </h2>

                <div className="p-6 bg-gradient-to-r from-tdb-primary to-tdb-secondary rounded-xl text-white mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Professional Plan</h3>
                      <p className="opacity-90">Unlimited JV submissions and team members</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">$99</div>
                      <div className="opacity-90">per month</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <div className="font-semibold text-gray-900">Next Billing Date</div>
                      <div className="text-sm text-gray-600">January 4, 2025</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <div className="font-semibold text-gray-900">Payment Method</div>
                      <div className="text-sm text-gray-600">Visa ending in 4242</div>
                    </div>
                    <button className="px-4 py-2 text-tdb-accent hover:bg-tdb-accent/10 rounded-lg transition-all font-semibold text-sm">
                      Update
                    </button>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t-2 border-gray-100 mt-6">
                  <button className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all font-semibold">
                    Cancel Subscription
                  </button>
                  <button className="px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all font-bold shadow-lg hover:shadow-xl">
                    Upgrade Plan
                  </button>
                </div>
              </div>

              {/* Billing History */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-tdb-primary mb-6">Billing History</h2>

                <div className="space-y-3">
                  {[
                    { date: 'Dec 4, 2024', amount: '$99.00', status: 'Paid' },
                    { date: 'Nov 4, 2024', amount: '$99.00', status: 'Paid' },
                    { date: 'Oct 4, 2024', amount: '$99.00', status: 'Paid' },
                  ].map((invoice, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all">
                      <div>
                        <div className="font-semibold text-gray-900">{invoice.date}</div>
                        <div className="text-sm text-gray-600">{invoice.amount}</div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {invoice.status}
                        </span>
                        <button className="px-4 py-2 text-tdb-accent hover:bg-tdb-accent/10 rounded-lg transition-all font-semibold text-sm">
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Integrations */}
          {activeTab === 'integrations' && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
                <Database className="w-5 h-5 text-tdb-accent" />
                <span>Connected Integrations</span>
              </h2>

              <div className="space-y-4">
                {/* Zapier Integration */}
                <div className="flex items-center justify-between p-6 border-2 border-gray-200 rounded-xl hover:border-tdb-accent transition-all">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Zapier</div>
                      <div className="text-sm text-gray-600">Automate workflows with 5000+ apps</div>
                    </div>
                  </div>
                  <button className="px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all font-semibold">
                    Connect
                  </button>
                </div>

                {/* Email Integration */}
                <div className="flex items-center justify-between p-6 border-2 border-gray-200 rounded-xl hover:border-tdb-accent transition-all">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Email Service</div>
                      <div className="text-sm text-gray-600">Connect your email provider for notifications</div>
                    </div>
                  </div>
                  <button className="px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all font-semibold">
                    Connect
                  </button>
                </div>

                {/* Webhook Integration */}
                <div className="flex items-center justify-between p-6 border-2 border-gray-200 rounded-xl hover:border-tdb-accent transition-all">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Custom Webhooks</div>
                      <div className="text-sm text-gray-600">Send data to external services</div>
                    </div>
                  </div>
                  <button className="px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] transition-all font-semibold">
                    Configure
                  </button>
                </div>
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-2">API Access</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Generate API keys to integrate TDB with your custom applications
                </p>
                <button className="px-4 py-2 bg-tdb-primary text-white rounded-lg hover:bg-tdb-secondary transition-all font-semibold text-sm">
                  Generate API Key
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
